// src/pages/setup/SetupIntro.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useAuth } from '../../contexts/AuthContext';
import { logEvent } from '../../utils/analytics';
import { createOnboardingData, markPersonalizationStatus } from '../../services/auth';
import logoImage from '../../assets/images/Logo-icon-Color-NB.png';

const SetupIntro: React.FC = () => {
  const navigate = useNavigate();
  const { confirmDestructive } = useConfirmDialog();
  const { refreshOnboardingStatus } = useAuth();
  const [isStarting, setIsStarting] = useState(false);

  // 追蹤：個人化設置開始
  useEffect(() => {
    try {
      logEvent('personalization_started');
    } catch (error) {
      // 靜默失敗
    }
  }, []);

  const handleGetStarted = () => {
    setIsStarting(true);
    setTimeout(() => {
      navigate('/setup/step-1');
    }, 300);
  };

  const handleSkip = async () => {
    const confirmSkip = await confirmDestructive(
      "Skip Personalization Setup?",
      "You can always set this up later. Skip for now?"
    );

    if (confirmSkip) {
      try {
        logEvent('personalization_skipped', { step: 'all' });
      } catch (error) {
        // 靜默失敗
      }

      setIsStarting(true);
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // 生成 Onboarding 種子資料（後端有冪等保護，不會重複建立）
      try {
        await createOnboardingData({ timeZone: userTimeZone, locale: 'en' });
      } catch (error) {
        // 失敗時仍繼續流程，不阻擋用戶
      }

      // 寫入 Firestore 永久跳過，並同步更新 AuthContext 狀態
      try {
        await markPersonalizationStatus(true); // 跳過 = 永久完成，不再顯示設置流程
        await refreshOnboardingStatus();
      } catch (error) {
        // 靜默失敗，不阻擋導航
      }

      navigate('/today', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* 處理中的覆蓋層 */}
      {isStarting && (
        <div className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-loading">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-brand-blue mx-auto"></div>
            <p className="text-gray-600">Setting up your first day...</p>
            <p className="text-sm text-gray-500">This will only take a moment</p>
          </div>
        </div>
      )}

      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-sm mx-auto">
          <div className="text-center space-y-10">

            {/* Logo */}
            <div className="flex justify-center animate-welcome-slide-up">
              <img
                src={logoImage}
                alt="Orlo"
                className="w-16 h-16 object-contain"
              />
            </div>

            {/* 動畫主視覺 */}
            <div className="flex justify-center animate-welcome-slide-up">
              <div className="relative w-36 h-10">

                {/* 書寫線條：left=12px 使視覺中心對齊容器中心（12 + 52 = 64 = 144/2） */}
                <div
                  className="absolute bg-sky-400 rounded"
                  style={{
                    top: '21px',
                    left: '12px',
                    width: '104px',
                    height: '2px',
                    transformOrigin: 'left center',
                    animation: 'grow-line 4s linear 0.2s infinite',
                  }}
                />

                {/* 外層：只負責左右平移，linear 與線條完全同步 */}
                <div
                  className="absolute top-0"
                  style={{ left: '12px', animation: 'pencil-translate 4s linear 0.2s infinite' }}
                >
                  {/* 內層：只負責筆尖擺動，transformOrigin 設在筆尖位置 */}
                  <div style={{ animation: 'pencil-rotate 0.6s ease-in-out 0.2s infinite', transformOrigin: '8% 92%' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </div>
                </div>

              </div>
            </div>

            {/* 文字區 */}
            <div className="space-y-3">
              <h1 className="text-2xl font-bold text-gray-900 leading-tight animate-welcome-slide-up" style={{ animationDelay: '0.3s', opacity: 0 }}>
                Let Orlo get to know you
              </h1>
              <p className="text-base text-gray-500 animate-welcome-slide-up" style={{ animationDelay: '0.5s', opacity: 0 }}>
                A few details make planning easier
              </p>
            </div>

            {/* 按鈕組 */}
            <div className="space-y-4 animate-welcome-slide-up" style={{ animationDelay: '0.7s', opacity: 0 }}>
              <button
                onClick={handleGetStarted}
                disabled={isStarting}
                className="w-full btn-primary py-4 px-6 rounded-xl font-semibold text-lg min-h-[56px]"
              >
                Get started →
              </button>

              <button
                onClick={handleSkip}
                disabled={isStarting}
                className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                Skip for now
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* 動畫 keyframes（inline style 注入） */}
      <style>{`
        @keyframes grow-line {
          /* scaleX + opacity 全由 GPU compositor 處理，與 translateX 同層渲染 */
          0%   { transform: scaleX(0); opacity: 1; }
          58%  { transform: scaleX(1); opacity: 1; }
          86%  { transform: scaleX(1); opacity: 1; }
          96%  { transform: scaleX(1); opacity: 0; }
          100% { transform: scaleX(0); opacity: 0; }
        }
        @keyframes pencil-translate {
          /* 0→58%：linear 勻速向右，與線條同步 */
          0%   { transform: translateX(0px);   animation-timing-function: linear; }
          58%  { transform: translateX(104px); }
          /* 抬筆暫停 */
          68%  { transform: translateX(104px); }
          /* 快速回位 */
          83%  { transform: translateX(0px);   }
          100% { transform: translateX(0px);   }
        }
        @keyframes pencil-rotate {
          /* 獨立的擺動節奏：以筆尖為支點 CW↔CCW */
          0%   { transform: rotate(-23deg); }
          50%  { transform: rotate(-13deg); }
          100% { transform: rotate(-23deg); }
        }
      `}</style>
    </div>
  );
};

export default SetupIntro;
